import React, { useMemo } from 'react';
import { TabState, PrimaryDataSet } from '../../../../types';
import { ROW_ID_COLUMN_ID } from '../../../../types/system-columns';
import { useProjectData } from '../../../stores/hooks/useProjectData';
import { useExcludedRows } from '../../../hooks/useExcludedRows';
import * as styles from '../../../styles/components/excluded-rows.css.ts';
import logger from '../../../utils/logger';

interface ExcludedRowsTabContentProps {
  tab: TabState;
}

export const ExcludedRowsTabContent: React.FC<ExcludedRowsTabContentProps> = () => {
  const { datasets } = useProjectData();
  
  // 現在選択されているプライマリデータセットを取得
  const primaryDatasets = useMemo(() => {
    return Object.values(datasets).filter(ds => ds.type === 'primary') as PrimaryDataSet[];
  }, [datasets]);
  
  // 最初のプライマリデータセットを使用（将来的には選択可能にする）
  const dataset = primaryDatasets[0];
  
  // 除外行管理フックを使用
  const {
    handleRestoreRows: onRestoreRows,
    handleClearAllExclusions: onClearAllExclusions,
    excludedRows
  } = useExcludedRows(dataset);
  
  const handleRestore = (rowNumber: number) => {
    if (onRestoreRows) {
      onRestoreRows([rowNumber]);
      logger.info('[ExcludedRowsTab] Restored row', { rowNumber });
    }
  };
  
  const handleRestoreAll = () => {
    if (onClearAllExclusions) {
      onClearAllExclusions();
      logger.info('[ExcludedRowsTab] Restored all excluded rows');
    }
  };
  
  const handleRestoreSelected = (rowNumbers: number[]) => {
    if (onRestoreRows && rowNumbers.length > 0) {
      onRestoreRows(rowNumbers);
      logger.info('[ExcludedRowsTab] Restored selected rows', { count: rowNumbers.length });
    }
  };
  
  const [selectedRows, setSelectedRows] = React.useState<Set<number>>(new Set());
  
  const toggleRowSelection = (rowNumber: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(rowNumber)) {
      newSelection.delete(rowNumber);
    } else {
      newSelection.add(rowNumber);
    }
    setSelectedRows(newSelection);
  };
  
  const selectAll = () => {
    setSelectedRows(new Set(excludedRows.map(row => row.rowNumber)));
  };
  
  const deselectAll = () => {
    setSelectedRows(new Set());
  };
  
  if (primaryDatasets.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.message}>
          No primary datasets available.
        </div>
      </div>
    );
  }
  
  if (!dataset) {
    return (
      <div className={styles.container}>
        <div className={styles.message}>
          Please select a dataset.
        </div>
      </div>
    );
  }
  
  if (excludedRows.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.message}>
          No excluded rows.
        </div>
      </div>
    );
  }
  
  // 表示する列を取得（最初の3列まで）
  const displayColumns = dataset?.schema.columns.slice(0, 4).filter(col => col.id !== ROW_ID_COLUMN_ID) || [];
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>Excluded Rows ({excludedRows.length})</h3>
        <div className={styles.actions}>
          <button 
            className={styles.actionButton}
            onClick={selectAll}
            disabled={selectedRows.size === excludedRows.length}
          >
            Select All
          </button>
          <button 
            className={styles.actionButton}
            onClick={deselectAll}
            disabled={selectedRows.size === 0}
          >
            Deselect All
          </button>
          <button 
            className={styles.primaryButton}
            onClick={() => handleRestoreSelected(Array.from(selectedRows))}
            disabled={selectedRows.size === 0}
          >
            Restore Selected ({selectedRows.size})
          </button>
          <button 
            className={styles.dangerButton}
            onClick={handleRestoreAll}
          >
            Restore All
          </button>
        </div>
      </div>
      
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxColumn}>
                <input
                  type="checkbox"
                  checked={selectedRows.size === excludedRows.length && excludedRows.length > 0}
                  onChange={(e) => e.target.checked ? selectAll() : deselectAll()}
                />
              </th>
              <th>Row #</th>
              {displayColumns.map(col => (
                <th key={col.id}>{col.name}</th>
              ))}
              <th>Exclusion Reason</th>
              <th>Excluded At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {excludedRows.map(row => (
              <tr key={row.rowNumber}>
                <td className={styles.checkboxColumn}>
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.rowNumber)}
                    onChange={() => toggleRowSelection(row.rowNumber)}
                  />
                </td>
                <td>{row.rowNumber + 1}</td>
                {displayColumns.map(col => (
                  <td key={col.id}>
                    {String(row.data[col.id] ?? '')}
                  </td>
                ))}
                <td>{row.comment || '-'}</td>
                <td>{new Date(row.excludedAt).toLocaleString()}</td>
                <td>
                  <button 
                    className={styles.restoreButton}
                    onClick={() => handleRestore(row.rowNumber)}
                  >
                    Restore
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};